/**
 * Source Card Component
 * Displays a single source reference with title, content type, and ordinal number
 * Uses ContainerButtonLink pattern from online-community for accessibility
 * Based on: online-community/Assets/projects/ReactRoot/src/AI/sources/SearchResultSource.tsx
 */
import '../../../../styles/oc-utilities.css';

import React, { useMemo } from 'react';

import ContainerButton from '../../../ContainerButton/ContainerButton';
import { MessageReference } from '../../../Message/types/messages';
import ContentTypeBadge, { ContentType } from './ContentTypeBadge';
import { useCrawlerJobs } from './CrawlerJobProvider';
import {
  BadgeContainer,
  CardContainer,
  ContentColumn,
  OrdinalColumn,
  Title,
  TitleLink,
} from './SourceCard.styled';

export type SourceCardProps = {
  source: MessageReference;
  ordinal: number;
  className?: string;
};

/**
 * Map AI item types to ContentType enum
 * Source of truth: online-community/Assets/projects/ReactRoot/src/AI/sources/SearchResultSource.tsx
 */
const AI_ITEM_TYPE_MAP: Record<string, ContentType> = {
  announcement: 'Announcement',
  answer: 'Answer',
  blog: 'Blog',
  event: 'CalendarEvent',
  calendarevent: 'CalendarEvent',
  community: 'Community',
  egroupmessage: 'MessagePost',
  eventoption: 'CalendarEvent',
  glossary: 'Glossary',
  ideation: 'Ideation',
  library: 'LibraryEntry',
  libraryentry: 'LibraryEntry',
  libraryentryfile: 'LibraryEntry',
  messagethread: 'MessageThread',
  navigation: 'Page',
  question: 'Question',
  volunteeropportunity: 'VolunteerOpportunity',
  automatedresponseref: 'AutomatedResponseRef',
};

/**
 * Line clamp component - limits text to N lines
 */
const LineClamp: React.FC<{ lines: number; children: React.ReactNode }> = ({ lines, children }) => (
  <div
    style={{
      display: '-webkit-box',
      WebkitLineClamp: lines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }}
  >
    {children}
  </div>
);

/**
 * SourceCard Component
 * Matches the pattern from SearchResultSource in online-community
 */
const SourceCard: React.FC<SourceCardProps> = ({ source, ordinal, className = '' }) => {
  const isAutomatedResponse = source.itemType === 'automatedresponseref';
  const contentType = AI_ITEM_TYPE_MAP[source.itemType.toLowerCase()] || source.itemType;
  const { getCrawlerNameByObjectType } = useCrawlerJobs();
  const crawlerName = getCrawlerNameByObjectType(source.itemType);

  const titleElement = useMemo(() => {
    if (isAutomatedResponse) {
      return (
        <div style={{flexGrow: 1}}>
        <Title>
          <LineClamp lines={3}>{source.title}</LineClamp>
        </Title></div>
      );
    }

    return (
      <TitleLink
        href={source.url}
        className="ai-link-contains-title"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Title>
          <LineClamp lines={3}>{source.title}</LineClamp>
        </Title>
      </TitleLink>
    );
  }, [isAutomatedResponse, source.title, source.url]);

  const content = useMemo(
    () => (
      <CardContainer $isClickable={!isAutomatedResponse}>
        {/* Ordinal number column */}
        <OrdinalColumn aria-hidden="true">{ordinal}</OrdinalColumn>

        {/* Content column */}
        <ContentColumn>
          {/* Title */}
          {titleElement}

          {/* Content type badge */}
          {contentType && (
            <BadgeContainer>
              <ContentTypeBadge contentType={contentType} customDisplay={crawlerName} />
            </BadgeContainer>
          )}
        </ContentColumn>
      </CardContainer>
    ),
    [ordinal, titleElement, contentType, isAutomatedResponse, crawlerName]
  );

  return isAutomatedResponse ? (
    <div className={`h-100 ${className}`} data-qa-id="source-card">
      {content}
    </div>
  ) : (
    <ContainerButton
      href={source.url}
      className={`ai-btn-contains-title ${className}`}
      contentClassName="h-100"
      qa-id="search-source-card-select"
      style={{ height: '100%', cursor: 'pointer' }}
    >
      {content}
    </ContainerButton>
  );
};

export default React.memo(SourceCard);
